import type {
  ApiDashboardElement,
  Collection,
  SourceMap
} from '@highimpact-frontend/api-types';

import { getSourceLabelFromMap } from '../api/sourceLabelKeys';

// -- type definitions --

type MeasurePointPath = {
  readonly measure_point_id: number;
  readonly measure_type_id: number;
};

type PrettyNamePair = [MeasurePointPath, string];

type ElementWithDataSourceToAxis = ApiDashboardElement & {
  readonly properties: {
    readonly data_source_to_axis?: Record<
      number,
      { readonly axisName?: string }
    >;
  };
};

// -- helper functions --

function hasMeasurePointDataSource(element: ApiDashboardElement): boolean {
  return (
    (element.element_type === 'plot' ||
      element.element_type === 'table' ||
      element.element_type === 'tablebycampaign') &&
    Array.isArray(element.data_source)
  );
}

function hasPersistedAxisMap(
  element: ApiDashboardElement
): element is ElementWithDataSourceToAxis {
  return (
    (element.element_type === 'plot' ||
      element.element_type === 'tablebycampaign') &&
    typeof element.properties === 'object' &&
    element.properties !== null &&
    'data_source_to_axis' in element.properties
  );
}

/**
 * Reads measure-point selection paths stored on a widget element.
 */
export function getMeasurePointPathsFromElement(
  element: ApiDashboardElement
): MeasurePointPath[] {
  if (!hasMeasurePointDataSource(element)) {
    return [];
  }

  return (element.data_source as MeasurePointPath[]).map((path) => ({
    measure_point_id: path.measure_point_id,
    measure_type_id: path.measure_type_id,
  }));
}

/**
 * Returns true when every saved source path has a persisted axis label on the element.
 */
export function elementHasPersistedAxisLabels(
  element: ApiDashboardElement,
  paths: readonly MeasurePointPath[]
): boolean {
  if (paths.length === 0 || !hasPersistedAxisMap(element)) {
    return false;
  }

  const axisByIndex = element.properties.data_source_to_axis ?? {};

  return paths.every((_, index) => Boolean(axisByIndex[index]?.axisName));
}

/**
 * Builds display labels from axis metadata already stored on the element JSON.
 */
export function createPrettyNamesFromPersistedAxisLabels(
  element: ApiDashboardElement,
  paths: readonly MeasurePointPath[]
): PrettyNamePair[] {
  if (!hasPersistedAxisMap(element)) {
    return [];
  }

  const axisByIndex = element.properties.data_source_to_axis ?? {};

  return paths.map((path, index) => [
    path,
    axisByIndex[index]?.axisName ??
      fallbackSourceLabel(path),
  ]);
}

function fallbackSourceLabel(path: MeasurePointPath): string {
  return `Point ${path.measure_point_id} (${path.measure_type_id})`;
}

/**
 * Returns true when any axis slot still uses the legacy `"Mesure"` placeholder.
 */
export function elementHasMesureAxisPlaceholders(
  element: ApiDashboardElement
): boolean {
  if (!hasPersistedAxisMap(element)) {
    return false;
  }

  const axisByIndex = element.properties.data_source_to_axis ?? {};

  return Object.values(axisByIndex).some((axis) => axis?.axisName === 'Mesure');
}

/**
 * Returns true when hydration must resolve labels via POST /api/config/sources/labels.
 */
export function elementNeedsSourceLabelResolution(
  element: ApiDashboardElement
): boolean {
  // Aggregation tables embed source labels in column config (source selector),
  // not in data_source_to_axis - the labels API is not used for hydration.
  if (element.element_type === 'table') {
    return false;
  }

  const paths = getMeasurePointPathsFromElement(element);

  if (paths.length === 0) {
    return false;
  }

  const plotType = (element.properties as { plot_type?: string })?.plot_type;
  if (element.element_type === 'plot' && plotType === 'bar') {
    return true;
  }

  return (
    !elementHasPersistedAxisLabels(element, paths) ||
    elementHasMesureAxisPlaceholders(element)
  );
}

/** @deprecated Use {@link elementNeedsSourceLabelResolution}. */
export const elementNeedsAssociationGraphForHydration =
  elementNeedsSourceLabelResolution;

/**
 * Flattens dashboard elements, expanding nested collections.
 */
export function flattenDashboardElements(
  elements: readonly ApiDashboardElement[]
): Exclude<ApiDashboardElement, Collection>[] {
  const flattenHelper = (
    acc: Exclude<ApiDashboardElement, Collection>[],
    element: ApiDashboardElement
  ): typeof acc => {
    if (element.element_type === 'collection') {
      return element.elements.flatMap((subElement) =>
        flattenHelper([], subElement)
      );
    }
    return acc.concat(element);
  };

  return elements.flatMap((element) => flattenHelper([], element));
}

/**
 * Returns true when any element on the dashboard needs batch label resolution.
 */
export function dashboardNeedsSourceLabelResolution(
  elements: readonly ApiDashboardElement[]
): boolean {
  return flattenDashboardElements(elements).some(
    elementNeedsSourceLabelResolution
  );
}

/** @deprecated Use {@link dashboardNeedsSourceLabelResolution}. */
export const dashboardNeedsAssociationGraph = dashboardNeedsSourceLabelResolution;

/**
 * Resolves hydration display labels from persisted axis metadata or the label map.
 */
export function resolvePrettyNamesForHydration(
  element: ApiDashboardElement,
  sourceLabelMap: SourceMap
): PrettyNamePair[] {
  const paths = getMeasurePointPathsFromElement(element);
  const plotType = (element.properties as { plot_type?: string })?.plot_type;

  if (plotType === 'bar') {
    return paths.map((path) => [
      path,
      getSourceLabelFromMap(sourceLabelMap, path) ?? fallbackSourceLabel(path),
    ]);
  }

  if (elementHasPersistedAxisLabels(element, paths)) {
    return createPrettyNamesFromPersistedAxisLabels(element, paths);
  }

  if (paths.length === 0) {
    return [];
  }

  return paths.map((path) => [
    path,
    getSourceLabelFromMap(sourceLabelMap, path) ?? fallbackSourceLabel(path),
  ]);
}

/**
 * Resolves a single axis label for legacy `"Mesure"` placeholders.
 */
export function resolveMesureAxisLabel(
  path: MeasurePointPath,
  sourceLabelMap: SourceMap
): string {
  return getSourceLabelFromMap(sourceLabelMap, path) ?? 'Mesure';
}

/** Legacy association graph retained only for transitional imports. */
export const HYDRATION_ASSOCIATION_DEPENDENT_STEPS = [] as const;
